import { Query } from "@cubejs-client/core";
import {
  FilterableDimensionConfig,
  ReportColumn,
  ReportColumnType,
  ReportConfiguration,
  ReportScope,
  TimeDimensionPosition,
  TimeDimensionType,
} from "@/reports/ReportModel";
import {
  MvProjectLinkAccessor,
  userLinkAccessorBuilder,
} from "@/reports/ReportLinkAccessors";
import { extractSortColumnsFromQuery } from "@/reports/organization/src/ReportUtils";

const query: Query = {
  measures: ["MvStudents.countryList"],
  timeDimensions: [
    {
      dimension: "MvStudents.createdAt",
    },
  ],
  order: {
    "MvStudents.createdAt": "desc",
  },
  filters: [
    {
      member: "MvStudents.ProjectParticipationUserCategoryCode",
      operator: "equals",
      values: ["student"],
    },
  ],
  dimensions: [
    "MvStudents.hash", // Required for LinkAccessor
    "MvStudents.pronoun",
    "MvStudents.participateInAnNonEndedProject",
    "MvStudents.hired",
    "MvStudents.email",
    "MvStudents.expiresAt",
    "MvStudents.activeDegree",
    // "Projects.id", // Required for LinkAccessor
    "MvStudents.availableForHireAt",
    "MvStudents.availableForInternshipAt",
    "MvStudents.name",
    "MvStudents.universityName",
    "MvStudents.gender",
    "MvStudents.race",
    "MvStudents.visa",
    "MvStudents.lawfulPermanentResidencyStatus",
    "MvStudents.exportControlStatus",
    "MvStudents.exitStatus",
    "SrcAccessCategoryTags.businessEntity",
    "SrcAccessCategoryTags.center",
    "SrcAccessCategoryTags.seismicshift",
    "SrcAccessCategoryTags.thrust",
    "SrcAccessCategoryTags.subthrust",
    "SrcAccessCategoryTags.theme",
    "SrcAccessCategoryTags.area",
    "SrcAccessCategoryTags.researchScholar",
    "SrcAccessCategoryTags.technicalTrack",
    "MvStudents.ProjectHash",
    "MvStudents.ProjectBusinessId",
    "MvStudents.ProjectName",
    "MvStudents.ProjectLeaderName",
    "MvStudents.ProjectLeaderHash",
    "MvStudents.ProjectStartsAt",
    "MvStudents.ProjectEndsAt",
    "MvStudents.hasPrivateProfile",
    "MvStudents.ResumeName",
    "MvStudents.ResumeId",
  ],
  segments: ["MvStudents.userNotDeleted", "SrcAccessCategoryTags.notDeleted"],
};

// order is important
const filterableDimensions: FilterableDimensionConfig[] = [
  {
    dimension: "MvStudents.name",
    label: "User Name",
  },
  {
    dimension: "MvStudents.exitStatus",
    label: "Student Exit Status",
  },

  {
    dimension: "MvStudents.gender",
    label: "Gender",
  },
  {
    dimension: "MvStudents.race",
    label: "Ethnicity",
  },
  {
    dimension: "MvStudents.country",
    label: "Citizenship",
  },

  {
    dimension: "MvStudents.visa",
    label: "US Visa Status",
  },
  {
    label: "Export Control Status",
    dimension: "MvStudents.exportControlStatus",
  },
  {
    dimension: "MvStudents.availableForHireAt",
    label: "Available for hire",
  },
  {
    dimension: "MvStudents.availableForInternshipAt",
    label: "Available for internship",
  },
  {
    dimension: "SrcAccessCategoryTags.businessEntity",
    label: "Business Entity",
  },
  {
    dimension: "SrcAccessCategoryTags.center",
    label: "Center",
  },
  {
    dimension: "SrcAccessCategoryTags.technicalTrack",
    label: "Technical Track",
  },
];

// order is important
const columns: ReportColumn[] = [
  {
    title: "Student ID",
    key: "MvStudents.hash",
    type: ReportColumnType.Text,
  },
  {
    title: "Student name",
    key: "MvStudents.name",
    type: ReportColumnType.Link,
    linkAccessor: userLinkAccessorBuilder("MvStudents.hash"),
  },
  {
    title: "Student email",
    key: "MvStudents.email",
    type: ReportColumnType.Text,
  },
  {
    title: "Student University",
    key: "MvStudents.universityName",
    type: ReportColumnType.Text,
  },
  {
    title: "Resume",
    key: "MvStudents.ResumeName",
    fileIdMember: "MvStudents.ResumeId",
    type: ReportColumnType.File,
  },
  {
    title: "Gender",
    key: "MvStudents.gender",
    type: ReportColumnType.Text,
  },
  {
    title: "Preferred Pronouns",
    key: "MvStudents.pronoun",
    type: ReportColumnType.Text,
  },
  {
    title: "Ethnicity",
    key: "MvStudents.race",
    type: ReportColumnType.Text,
  },
  {
    title: "US Visa status",
    key: "MvStudents.visa",
    type: ReportColumnType.Text,
  },
  {
    title: "Citizenships",
    key: "MvStudents.countryList",
    type: ReportColumnType.Text,
  },
  {
    title: "Green Card",
    key: "MvStudents.lawfulPermanentResidencyStatus",
    type: ReportColumnType.Text,
  },
  {
    title: "Export Control Status",
    key: "MvStudents.exportControlStatus",
    type: ReportColumnType.Text,
  },
  {
    title: "Business Entity",
    key: "SrcAccessCategoryTags.businessEntity",
    type: ReportColumnType.Text,
  },
  {
    title: "Seismic Shift",
    key: "SrcAccessCategoryTags.seismicshift",
    type: ReportColumnType.Text,
  },
  {
    title: "Thrust",
    key: "SrcAccessCategoryTags.thrust",
    type: ReportColumnType.Text,
  },
  {
    title: "Subthrust",
    key: "SrcAccessCategoryTags.subthrust",
    type: ReportColumnType.Text,
  },
  {
    title: "Theme",
    key: "SrcAccessCategoryTags.theme",
    type: ReportColumnType.Text,
  },
  {
    title: "Center",
    key: "SrcAccessCategoryTags.center",
    type: ReportColumnType.Text,
  },
  {
    title: "Science Area",
    key: "SrcAccessCategoryTags.area",
    type: ReportColumnType.Text,
  },
  {
    title: "Research Scholar",
    key: "SrcAccessCategoryTags.researchScholar",
    type: ReportColumnType.Text,
  },
  {
    title: "Technical Track",
    key: "SrcAccessCategoryTags.technicalTrack",
    type: ReportColumnType.Text,
  },
  {
    title: "Available for hire",
    key: "MvStudents.availableForHireAt",
    type: ReportColumnType.Date,
  },
  {
    title: "Available for internship",
    key: "MvStudents.availableForInternshipAt",
    type: ReportColumnType.Date,
  },
  {
    title: "Active Degree",
    key: "MvStudents.activeDegree",
    type: ReportColumnType.Boolean,
    labels: { true: "Yes", false: "No" },
  },
  {
    title: "Hired",
    key: "MvStudents.hired",
    type: ReportColumnType.Boolean,
    labels: { true: "Yes", false: "No" },
  },
  {
    title: "Active Participation",
    key: "MvStudents.participateInAnNonEndedProject",
    type: ReportColumnType.Boolean,
    labels: { true: "Yes", false: "No" },
  },
  {
    title: "Project ID",
    key: "MvStudents.ProjectBusinessId",
    type: ReportColumnType.Numeric,
  },
  {
    title: "Project Pillar ID",
    key: "MvStudents.ProjectHash",
    type: ReportColumnType.Numeric,
  },
  {
    title: "Project Name",
    key: "MvStudents.ProjectName",
    type: ReportColumnType.Link,
    linkAccessor: MvProjectLinkAccessor,
  },
  {
    title: "Project Leader",
    key: "MvStudents.ProjectLeaderName",
    type: ReportColumnType.Link,
    linkAccessor: userLinkAccessorBuilder("MvStudents.ProjectLeaderHash"),
  },
  {
    title: "Project Start Date",
    key: "MvStudents.ProjectStartsAt",
    type: ReportColumnType.Date,
  },
  {
    title: "Projects End Date",
    key: "MvStudents.ProjectEndsAt",
    type: ReportColumnType.Date,
  },
  {
    title: "Private Profile",
    key: "MvStudents.hasPrivateProfile",
    type: ReportColumnType.Boolean,
    labels: { true: "Yes", false: "No" },
  },
  {
    title: "Exit Status",
    key: "MvStudents.exitStatus",
    type: ReportColumnType.Text,
  },
  {
    title: "User Expiration Date",
    key: "MvStudents.expiresAt",
    type: ReportColumnType.Date,
  },
];

export const ScholarProjectParticipation407Config: ReportConfiguration = {
  name: "Scholar Project Participation (407)",
  section: "Student",
  scope: ReportScope.Organization,
  query,
  filterableDimensions,
  columns,
  hasDateFilters: true,
  timeDimensionType: TimeDimensionType.DayRange,
  timeDimension: {
    startsAt: "MvStudents.createdAt",
    endsAt: "MvStudents.createdAt",
  },
  sortColumns: extractSortColumnsFromQuery(query),
  zipExport: true,
  timeDimensionPosition: TimeDimensionPosition.Bottom,
};
